import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { useAction, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, qty, relative } from "@/lib/format";

interface Count {
  id: string;
  count_number: string;
  count_type: string;
  reason_process: string;
  warehouse_code?: string;
  freeze_mode: string;
  status: string;
  require_separate_approver: boolean;
  counted_by?: string;
  approved_by?: string;
  order_id?: string;
  task_id?: string;
  created_at: string;
  approved_at?: string;
}

type Tab = "open" | "submitted" | "all";

/**
 * Qamrov va sabab nomlari — SERVER qiymatlari emas, odam tili.
 *
 * Jadvalda `POINT`, `INVENTORY` kabi qiymatlar turardi va ular faqat
 * ishlab chiquvchiga tushunarli edi.
 *
 * HOLAT bu yerda tarjima qilinmaydi: uning lug'ati umumiy
 * (`lib/labels.ts`) va `StatusBadge` o'zi ishlatadi. Ikkinchi nusxa
 * bitta ekranda ikki xil yozuv berardi — «tasdiqlandi» va
 * «Yakunlandi» yonma-yon chiqardi.
 *
 * Noma'lum qiymat YO'QOLMAYDI — o'zi ko'rsatiladi.
 */
const TYPE_LABEL: Record<string, string> = {
  POINT: "Kataklar",
  FULL: "To'liq",
  DISCREPANCY: "Tafovut",
};

const REASON_LABEL: Record<string, string> = {
  INVENTORY: "rejali",
  PICKING: "terishda farq",
  CONTROL: "nazoratda farq",
  RECEIPT: "qabulda farq",
  PUTAWAY: "joylashtirishda farq",
  REPLENISHMENT: "to'ldirishda farq",
};

function label(table: Record<string, string>, value: string): string {
  return table[value] ?? value.toLowerCase();
}

/** Rejali sanash (INV-001): nuqtaviy — kataklar, to'liq — zona/ombor. */
interface PlanForm {
  operation_id: string;
  count_type: "POINT" | "FULL";
  cells: string;
  warehouse_code: string;
  zone_code: string;
  freeze_mode: "BLOCK" | "RECONCILE";
}

interface PlanResult {
  counts: Count[];
  empty_location_codes: string[];
}

/** `POST /inventory-counts/apply-pending` natijasi. */
interface ApplyPendingResult {
  applied: Count[];
  failed: Array<{ count_id: string; count_number: string; code: string; message: string }>;
  skipped_count: number;
}

// operation_id dialog ochilganda bir marta olinadi: qayta urinish
// ochilgan topshiriqlarni takrorlamaydi, qolganini ochadi.
const newPlan = (): PlanForm => ({
  operation_id: crypto.randomUUID(),
  count_type: "POINT",
  cells: "",
  warehouse_code: "A",
  zone_code: "",
  freeze_mode: "BLOCK",
});

/** Kataklar — har qatorda yoki vergul bilan. */
function parseCells(raw: string): string[] {
  return [...new Set(raw.split(/[\s,;]+/).map((code) => code.trim().toUpperCase()).filter(Boolean))];
}

// Server holatlari (inventory-count-service): COUNTING — sanalmoqda,
// REVIEW — topshirilgan, tasdiq kutilmoqda.
const TAB_STATUS: Record<Tab, string | undefined> = {
  open: "COUNTING",
  submitted: "REVIEW",
  all: undefined,
};

export function Counts() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [approving, setApproving] = useState<Count | null>(null);
  const [plan, setPlan] = useState<PlanForm | null>(null);
  const [planned, setPlanned] = useState<PlanResult | null>(null);
  const [applyingPending, setApplyingPending] = useState(false);
  const [applied, setApplied] = useState<ApplyPendingResult | null>(null);

  // Zonalar ro'yxati — «butun zona» tanlanganda. Kodni qo'lda
  // yozishda bitta harf xato bo'lsa sanash bo'sh chiqardi.
  const zones = useList<{ code: string; name?: string }>(
    "topology", "/api/v1/zones", { limit: 100 }, { enabled: plan !== null });

  const query = useList<Count>("count", "/api/v1/inventory-counts", {
    status: TAB_STATUS[tab],
    limit: 200,
  });

  const approve = useAction<Count, { count: Count; reason: string }>(
    "count",
    ({ count, reason }) => ({
      path: `/api/v1/inventory-counts/${count.id}/approve`,
      method: "POST",
      body: { note: reason },
    }),
  );

  // Tasdiq kutib qolgan sanashlar (TSD da yopilgan, qoldiq eski raqamda)
  // BITTA tugma bilan qoldiqqa qo'llanadi. Amal idempotent: takroriy
  // bosish qoldiqni ikki marta o'zgartirmaydi.
  const applyPending = useAction<ApplyPendingResult, void>("count", () => ({
    path: "/api/v1/inventory-counts/apply-pending",
    method: "POST",
    body: {},
  }));
  const pendingCount = useList<Count>("count", "/api/v1/inventory-counts", { status: "REVIEW", limit: 200 },
    { enabled: session.can(PERM.countApprove) });

  // Ish inventarizatorlar TSD navbatiga COUNT topshirig'i bo'lib tushadi.
  const createPlan = useAction<PlanResult, { form: PlanForm; note: string }>(
    "count",
    ({ form, note }) => ({
      path: "/api/v1/inventory-counts/plans",
      method: "POST",
      body: {
        operation_id: form.operation_id,
        count_type: form.count_type,
        freeze_mode: form.freeze_mode,
        ...(form.count_type === "POINT"
          ? { location_codes: parseCells(form.cells) }
          : { warehouse_code: form.warehouse_code, zone_code: form.zone_code.trim().toUpperCase() || undefined }),
        note,
      },
    }),
  );

  const canApprove = session.can(PERM.countApprove);
  const canCreate = session.can(PERM.countCreate);
  const pendingTotal = pendingCount.data?.items.length ?? 0;
  const needle = search.trim().toLowerCase();
  const rows = (query.data?.items ?? []).filter((row) =>
    needle
      ? [row.count_number, row.count_type, row.reason_process, row.warehouse_code]
          .some((value) => value?.toLowerCase().includes(needle))
      : true,
  );

  const columns: Array<Column<Count>> = [
    {
      key: "number",
      header: "Raqam",
      render: (row) => <span className="mono">{row.count_number}</span>,
    },
    { key: "type", header: "Qamrov", render: (row) => label(TYPE_LABEL, row.count_type) },
    {
      key: "process",
      header: "Nega",
      render: (row) => label(REASON_LABEL, row.reason_process),
    },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "created",
      header: "Yaratildi",
      render: (row) => <span title={dateTime(row.created_at)}>{relative(row.created_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) =>
        row.status === "REVIEW" && canApprove ? (
          <button
            type="button"
            className="btn btn-tonal btn-sm"
            onClick={() => setApproving(row)}
          >
            <Icon name="check" size={14} />
            Tasdiqlash
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Inventarizatsiya"
        hint="Sanash topshiriqlari. TSD da sanash yakunlangan zahoti qoldiq sanalgan miqdorga o'tadi; farq tafovut hisobotida va jurnalda qoladi."
        actions={
          <>
            {canApprove && pendingTotal > 0 && (
              <button
                type="button"
                className="btn btn-tonal"
                onClick={() => {
                  applyPending.reset();
                  setApplyingPending(true);
                }}
              >
                <Icon name="check" size={16} />
                Kutilganlarni qo'llash ({pendingTotal})
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-filled"
                onClick={() => {
                  createPlan.reset();
                  setPlan(newPlan());
                }}
              >
                <Icon name="plus" size={16} />
                Yangi inventarizatsiya
              </button>
            )}
          </>
        }
      />

      {applied && (
        <div className="notice" role="status" style={{ marginBottom: 12 }}>
          <Icon name={applied.failed.length > 0 ? "alert" : "check"} size={16} />
          <span>
            {applied.applied.length} ta sanash qoldiqqa qo'llandi
            {applied.applied.length > 0 && (
              <>
                {" "}(<span className="mono">{applied.applied.map((count) => count.count_number).join(", ")}</span>)
              </>
            )}
            .
            {applied.failed.length > 0 && (
              <>
                {" "}Qo'llanmadi:{" "}
                {applied.failed.map((failure) => `${failure.count_number} — ${failure.message}`).join("; ")}
              </>
            )}
          </span>
        </div>
      )}

      {planned && (
        <div className="notice" role="status" style={{ marginBottom: 12 }}>
          <Icon name="check" size={16} />
          <span>
            {planned.counts.length} ta sanash ochildi
            {planned.counts.length > 0 && (
              <>
                {" "}(<span className="mono">{planned.counts.map((count) => count.count_number).join(", ")}</span>)
              </>
            )}
            {" "}— ish inventarizatorlar TSD navbatiga tushdi.
            {planned.empty_location_codes.length > 0 && (
              <>
                {" "}Qoldig'i yo'q kataklar topshiriqqa kirmadi:{" "}
                <span className="mono">{planned.empty_location_codes.join(", ")}</span>
              </>
            )}
          </span>
        </div>
      )}

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Raqam, tur yoki ombor bo'yicha…"
        onRefresh={() => void query.refetch()}
        refreshing={query.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={setTab}
          options={[
            { value: "all", label: "Hammasi" },
            { value: "open", label: "Sanalmoqda" },
            { value: "submitted", label: "Tasdiq kutilmoqda" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        emptyTitle="Inventarizatsiya topilmadi"
        emptyHint="Sanash topshiriqlari kamomad yoki ortiqcha aniqlanganda avtomatik yaratiladi; rejali sanash — «Yangi inventarizatsiya»."
        hasMore={query.data?.has_more}
        footer={<span>{rows.length} ta sanash</span>}
      />

      <ConfirmDialog
        open={approving !== null}
        title="Sanash natijasini tasdiqlash"
        description={
          approving ? (
            <>
              <b>{approving.count_number}</b> natijasi tasdiqlanadi. Qoldiq
              to'g'rilanadi va <b>{approving.reason_process}</b> jarayoniga
              natija qaytariladi (INV-005). Bu amalni qaytarib bo'lmaydi.
            </>
          ) : null
        }
        confirmLabel="Tasdiqlash"
        requireReason
        reasonLabel="Izoh"
        reasonPlaceholder="Masalan: qayta sanaldi, farq tasdiqlandi"
        busy={approve.isPending}
        error={approve.error}
        wide
        extra={
          approving ? (
            <>
              <CountDiff countId={approving.id} />
              {approving.require_separate_approver && (
                <div className="notice">
                  <Icon name="shield" size={16} />
                  <span>
                    INV-004: sanagan xodim bu natijani tasdiqlay olmaydi — server
                    buni tekshiradi.
                  </span>
                </div>
              )}
            </>
          ) : null
        }
        onClose={() => {
          setApproving(null);
          approve.reset();
        }}
        onConfirm={(reason) => {
          if (!approving) return;
          approve.mutate(
            { count: approving, reason },
            {
              onSuccess: () => {
                setApproving(null);
                approve.reset();
              },
            },
          );
        }}
      />

      <ConfirmDialog
        open={applyingPending}
        title="Kutib qolgan sanashlarni qo'llash"
        description={
          <>
            Tasdiq kutayotgan <b>{pendingTotal}</b> ta sanash natijasi qoldiqqa
            qo'llanadi: har katak sanalgan miqdorga to'g'rilanadi, navbatdagi
            topshiriq yopiladi. Farq tafovut hisobotida va jurnalda qoladi.
            Takroriy bosish qoldiqni ikki marta o'zgartirmaydi.
          </>
        }
        confirmLabel="Qo'llash"
        busy={applyPending.isPending}
        error={applyPending.error}
        onClose={() => {
          setApplyingPending(false);
          applyPending.reset();
        }}
        onConfirm={() => {
          applyPending.mutate(undefined, {
            onSuccess: (result) => {
              setApplied(result);
              setApplyingPending(false);
            },
          });
        }}
      />

      <ConfirmDialog
        open={plan !== null}
        wide
        title="Yangi inventarizatsiya"
        description="Kataklar yurish tartibida bo'linadi va sanovchilarning TSD siga topshiriq bo'lib tushadi. Sanash vaqtida katak bloklanadi; TSD da sanash yakunlangach qoldiq sanalgan miqdorga avtomatik o'tadi."
        confirmLabel="Sanashni ochish"
        requireReason
        reasonLabel="Sabab"
        reasonPlaceholder="Masalan: oylik nuqtaviy tekshiruv"
        busy={createPlan.isPending}
        error={createPlan.error}
        confirmDisabled={plan?.count_type === "POINT" && parseCells(plan.cells).length === 0}
        extra={
          plan && (
            <>
              <label className="field">
                <span className="field-label">Nimani sanaymiz</span>
                <select
                  className="input"
                  value={plan.count_type}
                  onChange={(event) =>
                    setPlan({ ...plan, count_type: event.target.value as PlanForm["count_type"] })
                  }
                >
                  <option value="POINT">Bir nechta katak</option>
                  <option value="FULL">Butun zona yoki ombor</option>
                </select>
              </label>
              {plan.count_type === "POINT" ? (
                <label className="field">
                  <span className="field-label">Kataklar</span>
                  <textarea
                    className="input mono"
                    rows={4}
                    placeholder={"A01-02-03-04\nA01-02-03-05"}
                    value={plan.cells}
                    onChange={(event) => setPlan({ ...plan, cells: event.target.value })}
                  />
                  <span className="field-hint">
                    Har qatorda bittadan yoki vergul bilan · {parseCells(plan.cells).length} ta katak. Qoldig'i yo'q katak topshiriqqa kirmaydi.
                  </span>
                </label>
              ) : (
                <label className="field">
                  <span className="field-label">Zona</span>
                  <select
                    className="input"
                    value={plan.zone_code}
                    onChange={(event) => setPlan({ ...plan, zone_code: event.target.value })}
                  >
                    <option value="">Butun ombor</option>
                    {(zones.data?.items ?? []).map((zone) => (
                      <option key={zone.code} value={zone.code}>
                        {zone.name ? `${zone.code} — ${zone.name}` : zone.code}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">
                    Zona kodini qo'lda yozish shart emas — ro'yxatdan tanlanadi.
                  </span>
                </label>
              )}
            </>
          )
        }
        onClose={() => {
          setPlan(null);
          createPlan.reset();
        }}
        onConfirm={(note) => {
          if (!plan) return;
          createPlan.mutate(
            { form: plan, note },
            {
              onSuccess: (result) => {
                setPlanned(result);
                setPlan(null);
                createPlan.reset();
              },
            },
          );
        }}
      />
    </>
  );
}

interface CountLine {
  id: string;
  location_id: string;
  sku_id: string;
  expected_qty?: string;
  counted_qty?: string;
  delta_qty?: string;
}

/**
 * Tasdiqlanadigan farq: katak, tovar, kutilgan, sanalgan va farq.
 *
 * Qo'lda sinov topilmasi: tasdiq oynasi faqat hujjat raqamini
 * ko'rsatardi — menejer qoldiqni qanchaga o'zgartirayotganini
 * ko'rmasdan tasdiqlardi (INV-004 ma'nosini yo'qotardi).
 */
function CountDiff({ countId }: { countId: string }) {
  const lines = useList<CountLine>("count", `/api/v1/inventory-counts/${countId}/lines`, { limit: 500 });
  const items = lines.data?.items ?? [];
  const skuIds = [...new Set(items.map((line) => line.sku_id))].join(",");
  const locationIds = [...new Set(items.map((line) => line.location_id))].join(",");
  const skus = useList<{ id: string; article: string; name: string }>(
    "masterData", "/api/v1/skus", { ids: skuIds, limit: 500 }, { enabled: skuIds !== "" });
  const locations = useList<{ id: string; code: string }>(
    "topology", "/api/v1/locations", { ids: locationIds, limit: 500 }, { enabled: locationIds !== "" });
  const sku = new Map((skus.data?.items ?? []).map((item) => [item.id, item]));
  const cell = new Map((locations.data?.items ?? []).map((item) => [item.id, item.code]));

  if (lines.isLoading) return <div className="card skeleton" style={{ height: 80 }} />;
  if (items.length === 0) return null;
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Katak</th>
          <th>Tovar</th>
          <th style={{ textAlign: "right" }}>Kutilgan</th>
          <th style={{ textAlign: "right" }}>Sanaldi</th>
          <th style={{ textAlign: "right" }}>Farq</th>
        </tr>
      </thead>
      <tbody>
        {items.map((line) => {
          const delta = Number(line.delta_qty ?? 0);
          const item = sku.get(line.sku_id);
          return (
            <tr key={line.id}>
              <td className="mono">{cell.get(line.location_id) ?? line.location_id.slice(0, 8)}</td>
              <td>{item ? `${item.article} · ${item.name}` : <span className="mono">{line.sku_id.slice(0, 8)}</span>}</td>
              <td className="num" style={{ textAlign: "right" }}>{qty(line.expected_qty)}</td>
              <td className="num" style={{ textAlign: "right" }}>{qty(line.counted_qty)}</td>
              <td
                className="num"
                style={{ textAlign: "right", fontWeight: 600, color: delta < 0 ? "var(--error)" : delta > 0 ? "var(--warning)" : undefined }}
              >
                {delta > 0 ? `+${qty(line.delta_qty)}` : qty(line.delta_qty)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
